import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from './DashboardLayout';
import { getCashSessionReport } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import {
  ArrowLeft, Loader2, Printer, DollarSign, Banknote, CreditCard, ArrowRightLeft,
  Lock, Unlock, User, Receipt, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';

export default function AdminCashReportPage() {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    setLoading(true);
    getCashSessionReport(sessionId)
      .then((res) => setData(res.data))
      .catch((e) => setError(e?.response?.data?.detail || 'Error al cargar el corte de caja'))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="card-base p-10 text-center text-red-600" data-testid="cash-report-error">
          {error || 'Sin datos'}
        </div>
      </DashboardLayout>
    );
  }

  const session = data.session;
  const totals = data.totals || { by_method: {}, sales_total: 0, orders_count: 0 };
  const byOperator = data.by_operator || [];
  const expected = session.expected_amount ?? session.current_expected ?? 0;
  const closed = session.status === 'cerrada';

  const formatTime = (iso) =>
    iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' }) : '—';

  const handlePrint = () => window.print();

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn print:space-y-3" data-testid="cash-report-page">
        {/* Header & actions (hidden on print) */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 print:hidden">
          <div>
            <Link to="/admin/caja" className="text-sm text-blue-600 inline-flex items-center gap-1 hover:underline">
              <ArrowLeft className="w-4 h-4" /> Volver a Historial Caja
            </Link>
            <h1 className="text-3xl font-bold text-slate-900 mt-1">Corte de Caja</h1>
            <p className="text-slate-600 mt-1">
              {session.branch_name} · {session.date}
              {closed ? <span className="ml-2 inline-flex items-center gap-1 text-slate-500 text-sm"><Lock className="w-3.5 h-3.5" /> Cerrada</span>
                : <span className="ml-2 inline-flex items-center gap-1 text-emerald-600 text-sm"><Unlock className="w-3.5 h-3.5" /> Abierta</span>}
            </p>
          </div>
          <Button onClick={handlePrint} className="gap-2 bg-slate-900 hover:bg-slate-800 text-white" data-testid="print-cash-report-btn">
            <Printer className="w-4 h-4" /> Imprimir resumen
          </Button>
        </div>

        {/* Print header */}
        <div className="hidden print:block text-center border-b pb-3 mb-3">
          <h1 className="text-xl font-bold">Corte de Caja · WD Lavado</h1>
          <p className="text-sm">{session.branch_name} · {session.date}</p>
        </div>

        {/* Big total card */}
        <div className="rounded-2xl border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 p-6 text-center" data-testid="cash-report-total">
          <div className="text-sm font-semibold text-blue-600 uppercase tracking-wide">Total del turno</div>
          <div className="text-5xl font-bold text-blue-700 mt-2 tabular-nums">{formatCurrency(totals.sales_total)}</div>
          <div className="text-sm text-slate-600 mt-1">{totals.orders_count} {totals.orders_count === 1 ? 'venta' : 'ventas'} pagadas</div>
        </div>

        {/* Shift info + payment breakdown */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="card-base p-5" data-testid="cash-report-shift-info">
            <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3">Turno</h2>
            <div className="space-y-2 text-sm">
              <InfoRow icon={Unlock} label="Abrió" value={session.opened_by_name} sub={formatTime(session.opened_at)} />
              <InfoRow icon={Lock} label="Cerró" value={session.closed_by_name || '—'} sub={closed ? formatTime(session.closed_at) : 'En curso'} />
              <InfoRow icon={DollarSign} label="Apertura" value={formatCurrency(session.opening_amount || 0)} mono />
              {closed && (
                <>
                  <InfoRow icon={DollarSign} label="Esperado" value={formatCurrency(expected)} mono />
                  <InfoRow icon={DollarSign} label="Contado" value={session.closing_amount !== null && session.closing_amount !== undefined ? formatCurrency(session.closing_amount) : '—'} mono />
                  <InfoRow icon={DollarSign} label="Diferencia" value={session.difference !== null ? (session.difference >= 0 ? '+' : '') + formatCurrency(session.difference) : '—'} mono
                    valueClass={session.difference == null ? 'text-slate-400' : Math.abs(session.difference) < 0.01 ? 'text-slate-700' : session.difference > 0 ? 'text-emerald-600' : 'text-red-600'} />
                </>
              )}
            </div>
          </div>

          <div className="card-base p-5" data-testid="cash-report-by-method">
            <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3">Desglose de ingresos</h2>
            <div className="space-y-3">
              <MethodRow icon={Banknote} label="Efectivo" amount={totals.by_method.efectivo || 0} total={totals.sales_total} color="emerald" testId="method-efectivo" />
              <MethodRow icon={CreditCard} label="Tarjeta" amount={totals.by_method.tarjeta || 0} total={totals.sales_total} color="blue" testId="method-tarjeta" />
              <MethodRow icon={ArrowRightLeft} label="Transferencia" amount={totals.by_method.transferencia || 0} total={totals.sales_total} color="violet" testId="method-transferencia" />
            </div>
          </div>
        </div>

        {/* By operator */}
        <div className="card-base p-5" data-testid="cash-report-by-operator">
          <h2 className="text-sm font-semibold text-slate-500 uppercase mb-3 flex items-center gap-2">
            <User className="w-4 h-4" /> Servicios por operador
          </h2>
          {byOperator.length === 0 ? (
            <p className="text-slate-400 text-center py-6">Sin ventas en este turno</p>
          ) : (
            <div className="space-y-4">
              {byOperator.map((op) => (
                <div key={op.operator_id || 'sin'} className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`operator-block-${op.operator_id || 'sin'}`}>
                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-sm font-bold">
                        {op.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold text-slate-900">{op.name}</div>
                        <div className="text-xs text-slate-500">{op.orders_count} {op.orders_count === 1 ? 'servicio' : 'servicios'}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500">Subtotal</div>
                      <div className="text-lg font-bold text-blue-700 tabular-nums">{formatCurrency(op.total)}</div>
                    </div>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-100 bg-white">
                        <th className="text-left px-4 py-2 font-medium text-slate-500 text-xs uppercase">Folio</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500 text-xs uppercase">Cliente</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500 text-xs uppercase">Servicios</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500 text-xs uppercase">Método</th>
                        <th className="text-right px-4 py-2 font-medium text-slate-500 text-xs uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {op.orders.map((o) => (
                        <tr key={o.id} className="border-b border-slate-50 last:border-b-0">
                          <td className="px-4 py-2 font-mono text-blue-600 text-xs">{o.folio}</td>
                          <td className="px-4 py-2 text-slate-700">{o.client_name || '—'}</td>
                          <td className="px-4 py-2 text-slate-600 text-xs">
                            {o.services.map((s) => s.name).join(', ')}
                          </td>
                          <td className="px-4 py-2 capitalize text-slate-600">{o.payment_method}</td>
                          <td className="px-4 py-2 text-right font-semibold tabular-nums">{formatCurrency(o.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer print */}
        <div className="hidden print:block text-center text-xs text-slate-500 pt-4 border-t">
          Generado: {new Date().toLocaleString('es-MX')} · WD Lavado Automotriz
        </div>
      </div>
    </DashboardLayout>
  );
}

function InfoRow({ icon: Icon, label, value, sub, mono, valueClass }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2 text-slate-500">
        <Icon className="w-3.5 h-3.5" />
        <span>{label}</span>
      </div>
      <div className="text-right">
        <div className={`font-semibold ${mono ? 'font-mono' : ''} ${valueClass || 'text-slate-800'}`}>{value}</div>
        {sub && <div className="text-xs text-slate-400">{sub}</div>}
      </div>
    </div>
  );
}

function MethodRow({ icon: Icon, label, amount, total, color, testId }) {
  const pct = total > 0 ? Math.round((amount / total) * 100) : 0;
  const barColor = { emerald: 'bg-emerald-400', blue: 'bg-blue-400', violet: 'bg-violet-400' }[color];
  return (
    <div className="flex items-center gap-3" data-testid={testId}>
      <Icon className="w-4 h-4 text-slate-500" />
      <div className="flex-1">
        <div className="flex items-center justify-between mb-1">
          <span className="text-sm font-medium text-slate-700">{label}</span>
          <span className="text-sm font-semibold text-slate-900 tabular-nums">{formatCurrency(amount)}</span>
        </div>
        <div className="w-full bg-slate-100 rounded-full h-1.5">
          <div className={`h-1.5 rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <span className="text-xs text-slate-500 w-10 text-right">{pct}%</span>
    </div>
  );
}
